import { Dictionary, keyBy, uniq } from 'lodash';
import moment from 'moment';
import React, {
  FC,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Helmet } from 'react-helmet';
import { FormattedMessage } from 'react-intl';
import { ProjectId, Role, Team, User } from 'api/models';
import { Project } from 'api/project';
import { ResourceManagmentApi } from 'api/resource-management';
import {
  PlanningLevelConfig,
  Allocation,
  FetchableResourceType,
  ListOfResourcesToBeFetched,
  ResourceAllocations,
  ResourceManagementConfig,
  ResourcesToBeFetched,
  ResourceType,
} from 'api/resource-management/types';
import { PlaceholderCreateOrEditModal } from 'components/organisms/placeholders/create-edit-modal';
import {
  AddTeamsModal,
  AddUsersModal,
  useAddTeams,
  useAddUsersToProject,
  useFindMember,
} from 'components/organisms/resource-management/add-to-project';
import { FindMemberModal } from 'components/organisms/resource-management/add-to-project/find-member';
import { AutoAllocateSourceTasksDrawer } from 'components/organisms/resource-management/auto-allocation-source-tasks';
import {
  ExportAsCSVModal,
  useControlExport,
} from 'components/organisms/resource-management/export';
import { useProjectsViewFilter } from 'components/organisms/resource-management/filters';
import { ManageAllocationDrawer } from 'components/organisms/resource-management/manage-allocation';
import {
  CommonManagerProps,
  ManagerPropsWithLoadedRange,
  useAggregationManager,
  useAllocationLoader,
  useAllocationManager,
  useAllocationsMoreActionsManager,
  useAllocationWSManager,
  useAutoAllocationApiManager,
  useAutoAllocationStateManager,
  useProjectTimelineManager,
  useResourceManager,
  useResourcesLoader,
  useViewPresetManager,
} from 'components/organisms/resource-management/managers';
import { useAllocationStateManager } from 'components/organisms/resource-management/managers/allocation-state-manager';
import {
  SchedulerResourceType,
  TeamAsLeafResource,
  UserAsLeafResource,
} from 'components/organisms/resource-management/resource-types';
import { getCalendars } from 'components/organisms/resource-management/scheduler-config/calendars';
import { useColumnsConfigForGroupByProjects } from 'components/organisms/resource-management/scheduler-config/columns/columns-for-group-by-projects';
import { useEventsTooltip } from 'components/organisms/resource-management/scheduler-config/event-tooltips';
import {
  AutoAllocationStateChangeParams,
  useAutoAllocationStateChange,
  useConvertAllocations,
  useDeleteAllocations,
  useProjectRowHover,
  UserRemovedFromProjectParams,
  useShowSourceTasks,
  useTeamRemovedFromProject,
  useToStartOfProject,
  useUserRemovedFromProject,
} from 'components/organisms/resource-management/scheduler-config/listeners';
import {
  AllocationEventModel,
  GroupByProjectResourceModel,
} from 'components/organisms/resource-management/scheduler-config/models';
import {
  getZoomPresets,
  UserSelectablePresetIds,
  UserSelectablePresetIdsForHoursPerWeek,
  getZoomPresetsForHoursPerWeek,
} from 'components/organisms/resource-management/scheduler-config/view-presets/view-presets';
import { Scheduler } from 'components/organisms/resource-management/scheduler/scheduler';
import { SecondaryToolbar } from 'components/organisms/resource-management/secondary-toolbar';
import { ShiftProjectStartDate } from 'components/organisms/resource-management/shift-project-start-date';
import { ResourceManagementSocketConnector } from 'components/organisms/resource-management/socket-connector';
import {
  GroupBy,
  ProjectsWithMembers,
} from 'components/organisms/resource-management/types';
import { useLocalStorageState } from 'components/organisms/resource-management/use-localstorage-state';
import { usePageParam } from 'components/organisms/resource-management/use-page-params';
import {
  getProjectsFilteredByCurrentUser,
  getProjectsWithTeamMembersGroupedByRole,
} from 'components/organisms/resource-management/utils/associated-data';
import { getCorrectedDateRangeBasedOnView } from 'components/organisms/resource-management/utils/dates';
import { convertTimeOffToEvents } from 'components/organisms/resource-management/utils/events-conversion/allocation-by-timeoff';
import { convertAllocationsByUsersOrTeamsToEvents } from 'components/organisms/resource-management/utils/events-conversion/allocations-by-users-or-teams';
import { asAllocationPayload } from 'components/organisms/resource-management/utils/events-conversion/common';
import { convertProjectTimelineToEvents } from 'components/organisms/resource-management/utils/events-conversion/project-timeline-phases';
import {
  resourceIdForRole,
  resourceIdForTeamAsLeaf,
} from 'components/organisms/resource-management/utils/id';
import { convertProjectsToResourceTree } from 'components/organisms/resource-management/utils/resource-tree';
import {
  getMaximumEndDateFromListOfRanges,
  getMinimumStartDateFromListOfRanges,
} from 'components/organisms/resource-management/utils/resources-summary/common';
import { useTeamCellActions } from 'components/organisms/resource-management/utils/use-team-cell-actions';
import {
  DEFAULT_VIEW_ID,
  useGroupByProjectsDefaultViews,
  Views as ViewsComponent,
} from 'components/organisms/resource-management/views';
import { FilterableField } from 'components/organisms/tasks/fields';
import { WorkingDaysConfiguration } from 'components/organisms/user-capacity';
import { useFieldsAsGroupedFields } from 'components/pages/all-tasks/use-fields-as-grouped-fields';
import { GroupFieldsMeta } from 'components/pages/filter-transformer';
import { FilterGroupType } from 'components/pages/types';
import { useIsCurrentUserCollaborator } from 'hooks/permissions';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { createProjectPlaceholderMutationFn } from 'queries/placeholder';
import { fullName } from 'utils/full-name';
import { sortByText } from 'utils/sort-by-text';
import { ResourceManagementPage } from '../page';

interface Props {
  roles: Role[];
  users: User[];
  teams: Team[];
  projects: ProjectsWithMembers[];
  roleLookup: Dictionary<Role>;
  userLookup: Dictionary<User>;
  teamLookup: Dictionary<Team>;
  projectLookup: Dictionary<ProjectsWithMembers>;
  teamMembership: Record<string, Team[]>;
  workingDaysConfig: WorkingDaysConfiguration;
  projectFields: FilterableField[];
  isProjectFieldsLoading: boolean;
  statusFieldId?: number;
  resourceManagementConfig?: ResourceManagementConfig;
}

export const GroupByProjects: FC<Props> = ({
  roles,
  projects,
  users,
  teams,
  projectLookup,
  roleLookup,
  teamLookup,
  userLookup,
  teamMembership,
  workingDaysConfig,
  projectFields,
  isProjectFieldsLoading,
  statusFieldId,
  resourceManagementConfig,
}) => {
  const browserTitle = `${useTranslation(
    'resource_management.tabs.projects',
  )} - ${useTranslation('resource_management.title')}`;

  const schedulerRef = useRef<any>();
  const [searchText, setSearchText] = useState('');
  const [canInitializeWS, setInitializeWS] = useState(false);
  const [addUserToProjectId, setAddUserToProjectId] = useState<number>();
  const [addTeamToProjectId, setAddTeamToProjectId] = useState<number>();
  const [projectIdForShiftStartDate, setProjectIdforShiftStartDate] =
    useState<number>();

  const currentUser = useUser();
  const isCollaborator = useIsCurrentUserCollaborator();
  const { getAllocatedMinutesFromAggregation } = useAggregationManager();
  const { inviteMembersToProject } = useAddUsersToProject();
  const { addTeams } = useAddTeams();

  const projects_fields_lookup_by_name = useMemo(
    (): Record<string, FilterableField> =>
      keyBy(isProjectFieldsLoading ? [] : projectFields, 'fieldName'),
    [projectFields, isProjectFieldsLoading],
  );

  const { groupedFields } = useFieldsAsGroupedFields({
    projectFields,
  });

  const groupedLookupsByName: GroupFieldsMeta = useMemo(() => {
    return [
      {
        id: FilterGroupType.Project,
        fields: projects_fields_lookup_by_name,
      },
    ];
  }, [projects_fields_lookup_by_name]);

  const defaultViews = useGroupByProjectsDefaultViews(
    groupedLookupsByName,
    isProjectFieldsLoading,
  );

  const isHoursPerWeek = useMemo(
    () =>
      resourceManagementConfig?.planningLevelConfig ===
      PlanningLevelConfig.HoursPerWeek,
    [resourceManagementConfig?.planningLevelConfig],
  );

  const {
    pageParams,
    setPageParams,
    replacePageParams,
    toUrlFormat,
    fromUrlFormat,
  } = usePageParam(
    groupedLookupsByName,
    FilterGroupType.Project,
    isProjectFieldsLoading,
    isHoursPerWeek,
  );

  const isStateRestored = useLocalStorageState({
    loading: isProjectFieldsLoading,
    pageParams,
    replacePageParams,
    toUrlFormat,
    fromUrlFormat,
    key: 'resource_management.projects_view.page_param',
  });

  const onFilterReset = useCallback(() => {
    setSearchText('');
    const defaultView = defaultViews.find(
      ({ viewId }) => viewId === DEFAULT_VIEW_ID,
    );

    setPageParams({
      ...pageParams,
      ...defaultView?.viewDefinition,
      viewId: defaultView?.viewId,
    });
  }, [defaultViews, pageParams, setPageParams]);

  const zoomPresets = useMemo(
    () =>
      isHoursPerWeek
        ? getZoomPresetsForHoursPerWeek(workingDaysConfig)
        : getZoomPresets(workingDaysConfig),
    [workingDaysConfig, isHoursPerWeek],
  );

  const teamsInProject = useMemo(
    () =>
      teams.filter(
        (item) =>
          item.roleId &&
          item.roleId != null &&
          roleLookup[item.roleId] &&
          item.projectId === addTeamToProjectId,
      ),
    [addTeamToProjectId, roleLookup, teams],
  );

  const onUserInvited = useCallback(() => {
    setAddUserToProjectId(undefined);
  }, []);

  const { updateProjectTimeline, loadProjectPhases } =
    useProjectTimelineManager({
      schedulerRef,
      groupBy: GroupBy.Projects,
    });

  const onAllocationLoaded = useCallback(
    (
      resourceAllocations: ResourceAllocations,
      resourcesWithNewData: number[],
    ) =>
      new Promise<void>((resolve) => {
        const events = convertAllocationsByUsersOrTeamsToEvents(
          resourceAllocations,
          {
            excludeNonWorkingDays: true,
            workingDaysConfig,
          },
          resourceManagementConfig?.softAllocations.enabled,
        );

        const { allRecords } = schedulerRef.current.instance.resourceStore;
        const filteredRecords = allRecords.filter(
          (rec) => rec.type === SchedulerResourceType.User,
        );

        const timeOffEvents = convertTimeOffToEvents(
          filteredRecords,
          userLookup,
        );

        const allEvents = [...events, ...timeOffEvents];

        const nonDuplicateEvents = allEvents.filter(
          (event) =>
            !schedulerRef.current.instance.eventStore.isAvailable(event.id),
        );

        schedulerRef.current.instance.suspendRefresh();
        if (nonDuplicateEvents.length > 0) {
          schedulerRef.current.instance.eventStore.add(nonDuplicateEvents);
        }
        schedulerRef.current.instance.resourceStore.beginBatch();
        schedulerRef.current.instance.resources.forEach(
          ({ id, projectId, children, type: parentType }) => {
            if (parentType !== SchedulerResourceType.Project) {
              /**
               * Added this check coz when a project is expanded, children are also counted as resources
               * This is breaking when switching view with a resource expanded
               */
              //
              return;
            }
            const resource =
              schedulerRef.current.instance.resourceStore.getById(id);
            const totalMinutesForProject =
              getAllocatedMinutesFromAggregation(
                projectId,
              ).totalAllocatedMinutes;

            if (resource != null) {
              resource.set('totalAllocation', totalMinutesForProject);
              if (resourcesWithNewData.includes(projectId)) {
                resource.set('dataLoading', false);
              }
            }

            children.forEach(({ type, children: roleChildren }) => {
              if (type !== SchedulerResourceType.Role) {
                return;
              }

              (
                (roleChildren ?? []) as (
                  | UserAsLeafResource
                  | TeamAsLeafResource
                )[]
              ).forEach((childResource) => {
                const leafResource =
                  schedulerRef.current.instance.resourceStore.getById(
                    childResource.id,
                  );

                if (leafResource != null) {
                  leafResource.set(
                    'totalAllocation',
                    childResource.type === SchedulerResourceType.User
                      ? getAllocatedMinutesFromAggregation(projectId, {
                          type: ResourceType.User,
                          userId: childResource.userId,
                          roleId: childResource.roleId,
                          referenceTeamId: childResource.referenceTeam?.teamId,
                        }).totalAllocatedMinutes
                      : getAllocatedMinutesFromAggregation(projectId, {
                          type: ResourceType.Team,
                          teamId: childResource.teamId,
                        }).totalAllocatedMinutes,
                  );
                }
              });
            });
          },
        );
        schedulerRef.current.instance.resourceStore.endBatch();
        schedulerRef.current.instance.resumeRefresh(true);

        resolve();
      }),
    [
      getAllocatedMinutesFromAggregation,
      resourceManagementConfig?.softAllocations.enabled,
      userLookup,
      workingDaysConfig,
    ],
  );

  const getSchedulerProjectInfo = useCallback(() => {
    const projectsData = isCollaborator
      ? getProjectsFilteredByCurrentUser(projects, currentUser)
      : projects;
    const resources = convertProjectsToResourceTree(
      getProjectsWithTeamMembersGroupedByRole(
        projectsData,
        roles,
        isCollaborator ? {} : teamMembership,
      ),
      {
        includeAssignUserOrPlaceholder: !isCollaborator,
        showTeams: !isCollaborator,
      },
      statusFieldId,
    );

    return {
      resourceStore: {
        useRawData: true,
        data: resources,
        modelClass: GroupByProjectResourceModel,
        reapplySortersOnAdd: true,
      },
      eventStore: {
        useRawData: true,
        data: convertProjectTimelineToEvents(projects),
        modelClass: AllocationEventModel,
      },
      calendarManagerStore: {
        useRawData: true,
        data: getCalendars(workingDaysConfig),
      },
      calendar: 'nonWorkingDays',
    };
  }, [
    currentUser,
    isCollaborator,
    projects,
    roles,
    statusFieldId,
    teamMembership,
    workingDaysConfig,
  ]);

  const commonManagerProps: CommonManagerProps = useMemo(
    () => ({
      groupBy: GroupBy.Projects,
      users,
      teams,
      projects,
      roleLookup,
      teamLookup,
      userLookup,
      projectLookup,
      workingDaysConfig,
      teamMembership,
      schedulerRef,
      excludeNonWorkingDays: true,
      currentViewPreset: pageParams.viewPreset,
      resourceManagementConfig,
    }),
    [
      users,
      teams,
      projects,
      roleLookup,
      teamLookup,
      userLookup,
      projectLookup,
      workingDaysConfig,
      teamMembership,
      pageParams.viewPreset,
      resourceManagementConfig,
    ],
  );

  const { bulkUpdate, loadAllocations, sentRequestIds } =
    useAllocationStateManager(commonManagerProps);

  const {
    allocationModalParams,
    onEventActionCancel,
    onEventActionConfirm,
    onEventDelete,
    onEnableSplitMode,
  } = useAllocationManager({
    ...commonManagerProps,
    resourceManagementConfig,
    bulkUpdate,
  });

  useAllocationWSManager({
    sentRequestIds,
    ...commonManagerProps,
  });

  const {
    removeUserFromProjectInUI,
    addResource,
    removeTeamFromUI,
    removeAllocationsForResource,
  } = useResourceManager(commonManagerProps);

  const { isResourceStoreLoaded } = useResourcesLoader({
    schedulerRef,
    workingDaysConfig,
    projectInfoProvider: getSchedulerProjectInfo,
  });
  const [projectIdsForSocket, setProjectIdsForSocket] = useState<number[]>([]);

  const onVisibleResourceChange = useCallback(
    (visibleResources: ResourcesToBeFetched<ListOfResourcesToBeFetched>) => {
      setProjectIdsForSocket(
        uniq(
          projectIdsForSocket.concat(
            visibleResources[FetchableResourceType.ProjectList],
          ),
        ),
      );
    },
    [projectIdsForSocket],
  );

  const { isSchedulerLoadedWithData, rangesLoadedInUI } = useAllocationLoader({
    groupBy: GroupBy.Projects,
    schedulerRef,
    loadAllocations,
    currentViewPreset: pageParams.viewPreset,
    workingDaysConfig,
    onAllocationLoadHandler: onAllocationLoaded,
    onVisibleResourceChange,
  });

  useEffect(() => {
    if (isSchedulerLoadedWithData && !canInitializeWS) {
      setInitializeWS(isSchedulerLoadedWithData);
    }
  }, [canInitializeWS, isSchedulerLoadedWithData]);

  const rangeLoadedInUI = useMemo(
    () =>
      getCorrectedDateRangeBasedOnView(
        {
          fromDate: getMinimumStartDateFromListOfRanges(rangesLoadedInUI),
          toDate: getMaximumEndDateFromListOfRanges(rangesLoadedInUI),
        },
        pageParams.viewPreset,
        workingDaysConfig,
      ),
    [pageParams.viewPreset, rangesLoadedInUI, workingDaysConfig],
  );

  const commonPropsWithRange: ManagerPropsWithLoadedRange = useMemo(
    () => ({
      ...commonManagerProps,
      rangeLoadedInUI,
    }),
    [commonManagerProps, rangeLoadedInUI],
  );
  const { disableAutoAllocateForProject, enableAutoAllocateForProject } =
    useAutoAllocationStateManager({ schedulerRef, groupBy: GroupBy.Projects });

  const {
    enableAutoAllocation: enableAutoAllocationApi,
    disableAutoAllocation: disableAutoAllocationApi,
  } = useAutoAllocationApiManager();
  const enableAutoAllocation = useCallback(
    (projectId: number) => {
      enableAutoAllocationApi(projectId);
      enableAutoAllocateForProject(projectId);
    },
    [enableAutoAllocateForProject, enableAutoAllocationApi],
  );
  const disableAutoAllocation = useCallback(
    (projectId: number) => {
      disableAutoAllocationApi(projectId);
      disableAutoAllocateForProject(projectId);
    },
    [disableAutoAllocateForProject, disableAutoAllocationApi],
  );
  const onAutoAllocationStateChange = useCallback(
    ({ projectId, autoAllocation }: AutoAllocationStateChangeParams) => {
      if (autoAllocation) {
        enableAutoAllocation(projectId);
      } else {
        disableAutoAllocation(projectId);
      }
    },
    [disableAutoAllocation, enableAutoAllocation],
  );

  useAutoAllocationStateChange(schedulerRef, onAutoAllocationStateChange);

  const {
    deleteAllocationsForResource,
    convertAllocationsForResource,
    deleteAllocationHandler,
    convertAllocationHandler,
  } = useAllocationsMoreActionsManager(
    commonPropsWithRange,
    removeAllocationsForResource,
  );

  const { teamDataForFindMember, closeFindMemberModal, onFindMember } =
    useFindMember();

  const onTeamAdded = useCallback(
    (projectId: number, _teams: Team[]) => {
      schedulerRef.current.instance.suspendRefresh();
      _teams.forEach((team) =>
        addResource({ projectId, resource: { type: ResourceType.Team, team } }),
      );
      schedulerRef.current.instance.resumeRefresh(true);
      setAddTeamToProjectId(undefined);
    },
    [addResource],
  );

  const onTeamUpdate = useCallback(
    (team: Team) => {
      const { teamId, teamName, projectId, roleId } = team;

      if (projectId && roleId) {
        const resource = schedulerRef.current.instance.resourceStore.getById(
          resourceIdForTeamAsLeaf(projectId, teamId),
        );

        resource.set('teamName', teamName);
        const parentResource =
          schedulerRef.current.instance.resourceStore.getById(
            resourceIdForRole(projectId, roleId),
          );

        const beforeElement = parentResource.children.find(
          (res) =>
            sortByText(res.get('teamName') ?? fullName(res), teamName) === 1,
        );

        parentResource.insertChild(resource, beforeElement);
      }
    },
    [schedulerRef],
  );

  const { teamIdToBeUpdated, closeEditTeamModal, onEditTeam, updateTeamName } =
    useTeamCellActions({
      teamLookup,
      onTeamUpdate,
    });

  const columnConfig = useColumnsConfigForGroupByProjects({
    schedulerRef,
    softAllocationsEnabled:
      resourceManagementConfig?.softAllocations.enabled ?? false,
    onAddUsers: (projectId: number) => {
      setAddUserToProjectId(projectId);
    },
    onAddTeams: (projectId: number) => {
      setAddTeamToProjectId(projectId);
    },
    onFindMember,
    onEditTeam,
    onShiftProjectStartDate: (projectId: number) => {
      setProjectIdforShiftStartDate(projectId);
    },
    deleteAllocationHandler,
    convertAllocationHandler,
  });

  const { exportModalVisibility, hideExportModal, showExportModal, onExport } =
    useControlExport({
      groupBy: GroupBy.Projects,
      viewPreset: pageParams.viewPreset,
      filter: pageParams.filter,
      defaultGroup: FilterGroupType.Project,
    });

  useEventsTooltip(schedulerRef, userLookup);
  useDeleteAllocations(schedulerRef, deleteAllocationsForResource);
  useConvertAllocations(schedulerRef, convertAllocationsForResource);

  useToStartOfProject(
    schedulerRef,
    useCallback(
      ({ projectId }) => {
        const project = projects.find((x) => x.projectId === projectId);

        if (project == null) {
          return;
        }
        schedulerRef.current.instance.scrollToDate(
          moment(project.startDate).toDate(),
          {
            block: 'start',
          },
        );
      },
      [projects],
    ),
  );

  useUserRemovedFromProject(
    schedulerRef,
    useCallback(
      (params: UserRemovedFromProjectParams) =>
        removeUserFromProjectInUI({ ...params, rangeLoadedInUI }),
      [rangeLoadedInUI, removeUserFromProjectInUI],
    ),
  );

  useTeamRemovedFromProject(
    schedulerRef,
    useCallback(
      ({ projectId, teamId }) => {
        if (teamLookup[teamId] != null) {
          return removeTeamFromUI(
            projectId,
            teamLookup[teamId],
            rangeLoadedInUI,
          );
        }

        return undefined;
      },
      [rangeLoadedInUI, removeTeamFromUI, teamLookup],
    ),
  );

  useViewPresetManager({
    loading: !isStateRestored,
    schedulerRef,
    currentPresetId: pageParams.viewPreset,
    onPresetChange: useCallback(
      (viewPreset) => {
        setPageParams({
          ...pageParams,
          viewPreset,
        });
      },
      [pageParams, setPageParams],
    ),
  });

  useProjectsViewFilter({
    canApplyFilter: isResourceStoreLoaded,
    searchText,
    schedulerRef,
    filter: pageParams.filter,
    projectLookup,
    onFilterReset,
  });

  useProjectRowHover(
    schedulerRef,
    useCallback(
      ({ projectId }) => loadProjectPhases(projectLookup[projectId]),
      [loadProjectPhases, projectLookup],
    ),
  );

  const [showSourceTasksForAllocation, setAllocationForShowingSourceTasks] =
    useState<Allocation>();
  const sourceTasksGetter = useCallback(
    (allocation) =>
      ResourceManagmentApi.getSourceTasksForAllocationId(allocation.id),
    [],
  );

  useShowSourceTasks(schedulerRef, ({ event }) =>
    setAllocationForShowingSourceTasks(asAllocationPayload(event)),
  );

  return (
    <>
      <Helmet>
        <title>{browserTitle}</title>
      </Helmet>
      <ResourceManagementPage
        currentGroupBy={GroupBy.Projects}
        currentViewPreset={pageParams.viewPreset}
        actions={{
          primary: (
            <ViewsComponent
              groupBy={GroupBy.Projects}
              isFieldsLoading={isProjectFieldsLoading}
              lookup={groupedLookupsByName}
              currentDefinition={{
                filter: pageParams.filter,
              }}
              currentViewId={pageParams.viewId ?? DEFAULT_VIEW_ID}
              fields={groupedFields}
              defaultViews={defaultViews}
              onViewSelect={useCallback(
                ({ viewId, viewDefinition }) =>
                  setPageParams({ ...pageParams, ...viewDefinition, viewId }),
                [pageParams, setPageParams],
              )}
              onFilterChange={useCallback(
                (filter) => setPageParams({ ...pageParams, filter }),
                [pageParams, setPageParams],
              )}
            />
          ),
          secondary: (
            <SecondaryToolbar
              zoomPresets={zoomPresets}
              viewPresetIds={
                isHoursPerWeek
                  ? UserSelectablePresetIdsForHoursPerWeek
                  : UserSelectablePresetIds
              }
              currentPreset={pageParams.viewPreset}
              searchText={searchText}
              onViewPresetChange={(presetId) => {
                schedulerRef.current.instance.viewPreset = presetId;
              }}
              zoomIn={() => {
                schedulerRef.current.instance.zoomIn();
              }}
              zoomOut={() => {
                schedulerRef.current.instance.zoomOut();
              }}
              shiftNext={() => {
                schedulerRef.current.instance.shiftNext();
              }}
              shiftPrevious={() => {
                schedulerRef.current.instance.shiftPrevious();
              }}
              goToToday={() => {
                schedulerRef.current.instance.scrollToNow({
                  block: 'center',
                });
              }}
              onSearchChange={(text) => setSearchText(text)}
              onExport={isCollaborator ? undefined : showExportModal}
            />
          ),
        }}
        content={
          <>
            <Scheduler
              ref={schedulerRef}
              columns={columnConfig}
              newEventAllowedOn={useMemo(
                () => [SchedulerResourceType.User, SchedulerResourceType.Team],
                [],
              )}
              zoomPresets={zoomPresets}
              workingDaysConfig={workingDaysConfig}
              resourceManagementConfig={resourceManagementConfig}
            />
            {allocationModalParams != null ? (
              <ManageAllocationDrawer
                userLookup={userLookup}
                projectLookup={projectLookup}
                teamLookup={teamLookup}
                allocationDetail={allocationModalParams}
                softAllocationsEnabled={
                  resourceManagementConfig?.softAllocations.enabled ?? false
                }
                onClose={onEventActionCancel}
                onDone={onEventActionConfirm}
                onDelete={onEventDelete}
                onEnableSplitMode={onEnableSplitMode}
                workingDaysConfig={workingDaysConfig}
                isHoursPerWeek={isHoursPerWeek}
              />
            ) : null}
            <AddUsersModal
              projectId={addUserToProjectId}
              projectLookup={projectLookup}
              onUsersAdded={onUserInvited}
              onCancel={() => {
                setAddUserToProjectId(undefined);
              }}
              onInviteHandler={inviteMembersToProject}
            />
            <AddTeamsModal
              teamMembership={teamMembership}
              projectLookup={projectLookup}
              projectId={addTeamToProjectId}
              onTeamsAddHandler={addTeams}
              onInvited={onTeamAdded}
              teamsAvailableForProject={teamsInProject}
              createTeamHandler={
                addTeamToProjectId
                  ? createProjectPlaceholderMutationFn(addTeamToProjectId)
                  : () => Promise.resolve({} as Team)
              }
              onCancel={() => {
                setAddTeamToProjectId(undefined);
              }}
            />
            {teamDataForFindMember != null ? (
              <FindMemberModal
                teamData={teamDataForFindMember}
                onClose={closeFindMemberModal}
                projectLookup={projectLookup}
                roleLookup={roleLookup}
              />
            ) : null}

            {teamIdToBeUpdated ? (
              <PlaceholderCreateOrEditModal
                team={teamLookup[teamIdToBeUpdated]}
                roles={roles}
                placeholders={[]}
                primaryButtonLabel={<FormattedMessage id="save" />}
                save={updateTeamName}
                cancel={closeEditTeamModal}
              />
            ) : null}
            <ShiftProjectStartDate
              projectId={projectIdForShiftStartDate}
              onProjectShift={useCallback(
                (project: Project) => {
                  setProjectIdforShiftStartDate(undefined);
                  updateProjectTimeline(ProjectId(project.projectId));
                },
                [updateProjectTimeline],
              )}
              onCancel={() => setProjectIdforShiftStartDate(undefined)}
            />
            <ExportAsCSVModal
              visible={exportModalVisibility}
              onCancel={hideExportModal}
              onExportFinished={hideExportModal}
              exportHandler={onExport}
            />
            {canInitializeWS ? (
              <ResourceManagementSocketConnector
                {...commonPropsWithRange}
                projectIds={projectIdsForSocket}
              />
            ) : null}
            <AutoAllocateSourceTasksDrawer
              schedulerRef={schedulerRef}
              userLookup={userLookup}
              teamLookup={teamLookup}
              projectLookup={projectLookup}
              allocation={showSourceTasksForAllocation}
              sourceTasksGetter={sourceTasksGetter}
              onCancel={() => setAllocationForShowingSourceTasks(undefined)}
              isHoursPerWeek={isHoursPerWeek}
              workingDaysConfig={workingDaysConfig}
            />
          </>
        }
      />
    </>
  );
};
